import React, { FunctionComponent } from 'react';
// import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'
// import useMediaQuery from '@mui/material/useMediaQuery';

// import { check_subdomain } from '../../lib/server_helper';
import { fetch_all_with_count } from "../../../../lib/v31lib";
import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';

import {
  Box,
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  Dt,
  Typography
} from '../../../../components/v2/styled';

import { User } from '../../../../models/User';
import { KnowledgeCourse } from '../../../../models/KnowledgeCourse';
import { KnowledgeSubscription } from '../../../../models/KnowledgeSubscription';

// const { DateTime } = require("luxon");

import HelpersPractice from '../../../../actions/helpers/practice';
import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import Euro from '@mui/icons-material/Euro';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';

// Slashed-euro mark for an unpaid payable course — MUI has no "euro with
// strike", so we render the regular Euro icon with a line-through overlay
// (same visual language as the dimmed/struck rows elsewhere in the widgets).
// The caller's `sx` (colour/size) is applied to the Box; the icon and the
// slash both inherit from it via currentColor.
const EuroOff = (props: any) => {
  const { sx, ...rest } = props;
  return <Box sx={{position: 'relative', display: 'inline-flex', ...sx}}>
    <Euro {...rest} sx={{fontSize: 'inherit', color: 'inherit'}} />
    <Box sx={{
      position: 'absolute',
      left: '-2px',
      right: '-2px',
      top: '50%',
      height: '1.5px',
      backgroundColor: 'currentColor',
      transform: 'rotate(-45deg)',
    }} />
  </Box>;
};

type Props = {
  object: User;
  setPremiumKnowledgeOpen: (b:boolean) => void;
}

export const CoachUserWidgetsCoursesSmall: FunctionComponent<Props> = ({
  object,
  setPremiumKnowledgeOpen
}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  // const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);
  // const params = useParams();
  // const queryClient = useQueryClient();
  // const { state } = useLocation();
  // const [searchParams, setSearchParams] = useSearchParams();

  let kcCount = 0;
  let kcSubCount = 0;

  let is_knowledge = HelpersPractice.practice_modules_v3({practice: currentPractice, check: 'knowledge'});

  // Coach-level widget settings (the `cta` goto-button toggle and the `mode`
  // body picker, defaults on/stats). Stored on
  // login.settings.userdetails_widgets.settings.
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const cfg = widgetSettings?.[HelpersClientWidgets.coursesSmallValueKey];
  const showCta = HelpersClientWidgets.getCoursesSmallCtaEnabled(cfg);
  const mode = HelpersClientWidgets.getCoursesSmallMode(cfg);

  // Count-only queries feeding the stats body. `d.count` is the total matching
  // the filter regardless of per_page. Only fetched in stats mode.
  const {
    // data: kcs,
    // isLoading: objectsLoading,
    // isError: objectsError,
    // isSuccess: objectsSuccess,
    // refetch: objectsRefetch,
  } = useQuery({
    queryKey: ["client", "knowledge_courses", object?.id],
    queryFn: () =>
      fetch_all_with_count<KnowledgeCourse>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'knowledge_course',
          fields: ['id', 'name', 'knowsub'],
          sub_fields: {
            knowsub: ['id', 'last_activity', 'approved', 'approved_at', 'lesson_key']
          },
          order: 'id DESC',
          per_page: 1,
          page: 0,
          filter: {
            search: '',
            advanced: {
              practice_id: (login?.practice_id || currentPractice?.id)
            }
          }
        },
        login
      ),
      select: (d) => {
        kcCount = d.count;
        return d.result;
      },
    enabled: mode === 'stats' && !!(login?.practice_id || currentPractice?.id) && !!object?.id,
  });
  const {
    // data: kcSubs,
    // isLoading: objectsLoading,
    // isError: objectsError,
    // isSuccess: objectsSuccess,
    // refetch: objectsRefetch,
  } = useQuery({
    queryKey: ["client", "knowledge_courses_part", object?.id],
    queryFn: () =>
      fetch_all_with_count<KnowledgeCourse>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'knowledge_course',
          fields: ['id', 'name', 'knowsub'],
          sub_fields: {
            knowsub: ['id', 'last_activity', 'approved', 'approved_at', 'lesson_key']
          },
          order: 'id DESC',
          per_page: 1,
          page: 0,
          filter: {
            search: '',
            advanced: {
              for_client: object?.id,
              participating: 1,
              skip_hidden: 1,
              for_practice: (login?.practice_id || currentPractice?.id)
            }
          }
        },
        login
      ),
      select: (d) => {
        kcSubCount = d.count;
        return d.result;
      },
    enabled: mode === 'stats' && !!(login?.practice_id || currentPractice?.id) && !!object?.id,
  });

  // Last-5 list body: the client's course subscriptions by last_activity,
  // newest first. Only fetched in list mode.
  const {
    data: lastSubscriptions,
  } = useQuery({
    queryKey: ["client", "knowledge_subs_last", object?.id],
    queryFn: () =>
      fetch_all_with_count<KnowledgeSubscription>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'knowledge_subscription',
          fields: ['id', 'created_at', 'last_activity', 'knowledge_course', 'mollie_data'],
          sub_fields: {
            knowledge_course: ['id', 'name', 'mollie_data']
          },
          order: 'last_activity DESC',
          per_page: 5,
          page: 0,
          filter: {
            search: '',
            advanced: {
              user_id: object?.id,
              practice_id: (login?.practice_id || currentPractice?.id)
            }
          }
        },
        login
      ),
      select: (d) => d.result,
    enabled: mode === 'list' && !!(login?.practice_id || currentPractice?.id) && !!object?.id,
  });

  const lastFive = (lastSubscriptions || []).slice(0, 5);

  return <Paper sx={{padding: 2, marginTop: 1, position: 'relative'}}>
    {/* CTA: small text button top-right, absolute in the title row
        (space-saving — the old fullwidth bottom button cost a full row).
        Gated by the coach's `cta` setting; the premium lock uses the v2
        Button's built-in outlined + star treatment (knowledge module). */}
    {showCta && <Button
      label={t("coach.general.actions.to_courses")}
      id="goto_courses"
      text
      size="small"
      premiumLocked={!is_knowledge}
      startIcon={<ArrowRightAltIcon />}
      sx={{position: 'absolute', top: 4, right: 4, marginTop: 0, zIndex: 2}}
      onClick={() => {
        if (!!is_knowledge) {
          navigate(`/user/${object.id}/courses`);
        } else {
          setPremiumKnowledgeOpen(true);
        }
      }}
    />}

    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.courses")}</Typography>
        <Divider />
      </Grid>

      {/* Stats body: one i18n plural pair per metric (participating / total),
          separated by a middot. Only shown when the practice has at least one
          course (matches the old title-count guard on kcCount). */}
      {mode === 'stats' && kcCount > 0 && <Grid item xs={12}>
        <Typography variant="body2" html>
          {`${t("coach.submenu.detail.courses_participating_count", {count: kcSubCount})} · ${t("coach.submenu.detail.courses_total_count", {count: kcCount})}`}
        </Typography>
      </Grid>}

      {/* List body: last 5 course subscriptions (newest activity first), a
          Divider between consecutive rows. Row shape mirrors the
          questionnaires widget's list: name + a captioned activity date.
          When the course is payable (`mollie_data.cost` non-zero), a
          trailing euro mark shows the payment state: filled euro (primary)
          when the subscription itself records a payment (`mollie_data.paid_at`
          on the sub), slashed euro (secondary) when not. Free / cost-0
          courses carry no mark — there's no payment to report. */}
      {mode === 'list' && lastFive.map((sub, idx) => {
        const payable = parseFloat(sub.knowledge_course?.mollie_data?.cost || '') > 0;
        return <React.Fragment key={sub.id}>
          <Grid item xs={12} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Grid container spacing={0} sx={{flex: 1, minWidth: 0}}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" noWrap>
                  {sub.knowledge_course?.name || '-'}
                </Typography>
              </Grid>
              {!!sub.last_activity && <Grid item xs={12}>
                <Typography variant="caption" sx={{color: 'text.secondary'}}>
                  {t("coach.models.knowledge_courses.last_activity")}: <Dt d={sub.last_activity} f="DDD t" />
                </Typography>
              </Grid>}
            </Grid>
            {payable && (!!sub.mollie_data?.paid_at ? <Euro sx={{fontSize: '1.1rem', color: 'primary.main'}} /> : <EuroOff sx={{fontSize: '1.1rem', color: 'secondary.main'}} />)}
          </Grid>
          {idx < lastFive.length - 1 && <Grid item xs={12}>
            <Divider />
          </Grid>}
        </React.Fragment>;
      })}
    </Grid>
  </Paper>;
}
